import { useInfiniteQuery } from "@tanstack/react-query"
import type { ProductVisualKind } from "@nova-ai/product-visual-templates/kinds"
import { routes, type ProductVisualImagesResponse } from "@nova-ai/shared"

import { apiFetch } from "../lib/api"
import { queryKeys } from "./query-keys"

const PRODUCT_VISUAL_IMAGES_PAGE_SIZE = 24

export type ProductVisualImagesFilter = {
  store: string
  /** Images are indexed per visual kind; the grid always browses exactly one. */
  kind: ProductVisualKind
}

export function useProductVisualImages(filter: ProductVisualImagesFilter, enabled = true) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.productVisualImages(filter),
    enabled: enabled && filter.store.length > 0,
    initialPageParam: 1,
    queryFn: async ({ pageParam, signal }) => {
      const result = await apiFetch<ProductVisualImagesResponse>(routes.productVisuals.images(), {
        signal,
        query: {
          store: filter.store,
          kind: filter.kind,
          page: String(pageParam),
          pageSize: String(PRODUCT_VISUAL_IMAGES_PAGE_SIZE),
        },
      })
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
    getNextPageParam: (lastPage) =>
      lastPage.page * lastPage.pageSize < lastPage.total ? lastPage.page + 1 : undefined,
  })
  return {
    ...query,
    images: query.data?.pages.flatMap((page) => page.items) ?? [],
    total: query.data?.pages[0]?.total ?? 0,
    counts: query.data?.pages[0]?.counts,
  }
}
